import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react'
import type { TFunction } from 'i18next'
import type { OrgCaseEntry, OrgCasesByOwner } from '../../types/dashboard'
import {
  DEFAULT_BADGE,
  PRIORITY_COLOURS_DARK,
  PRIORITY_COLOURS_LIGHT,
  STATUS_COLOURS_DARK,
  STATUS_COLOURS_LIGHT,
  formatBytes,
  formatRelativeTime,
  type Theme,
} from './org-cases-page.helpers'

export function LoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="db-spinner h-8 w-8 animate-spin rounded-full border-4" />
        <p style={{ color: 'var(--text-muted)' }}>{t('orgCases.loadingCases')}</p>
      </div>
    </div>
  )
}

interface ErrorStateProps {
  message: string
  onRetry: () => void
  t: TFunction<'cases'>
}

export function ErrorState({ message, onRetry, t }: ErrorStateProps): ReactElement {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
          <AlertCircle className="h-10 w-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--text)' }}>
          {t('orgCases.failedToLoad')}
        </h1>
        <p className="mt-2 max-w-md" style={{ color: 'var(--text-dim)' }}>
          {message}
        </p>
        <button
          onClick={onRetry}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 font-medium text-white transition-colors hover:bg-[#5c6e94]"
        >
          <RefreshCw className="h-4 w-4" />
          {t('actions.retry')}
        </button>
      </div>
    </div>
  )
}

interface PageHeaderProps {
  totalCount: number
  ownersCount: number
  loading: boolean
  canCreateCase: boolean
  onRefresh: () => void
  onNewCase: () => void
  t: TFunction<'cases'>
}

export function PageHeader(props: PageHeaderProps): ReactElement {
  const { totalCount, ownersCount, loading, canCreateCase, onRefresh, onNewCase, t } = props
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--text)' }}>
          {t('orgCases.title')}
        </h1>
        <p className="mt-1" style={{ color: 'var(--text-dim)' }}>
          {t('orgCases.subtitle', { count: totalCount, members: ownersCount })}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onRefresh}
          disabled={loading}
          className="rounded-lg border p-2.5 transition-colors disabled:opacity-50"
          style={{ borderColor: 'var(--border-hover)', color: 'var(--text-dim)' }}
          title={t('actions.refresh')}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          onClick={onNewCase}
          disabled={!canCreateCase}
          className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2.5 font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {t('management.newCase')}
        </button>
      </div>
    </div>
  )
}

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  t: TFunction<'cases'>
}

export function SearchBar({ value, onChange, t }: SearchBarProps): ReactElement {
  return (
    <div className="relative max-w-md">
      <Search
        className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
        style={{ color: 'var(--text-muted)' }}
      />
      <input
        type="text"
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        placeholder={t('orgCases.searchPlaceholder')}
        className="w-full rounded-lg border py-2.5 pr-4 pl-10 transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
        style={{
          borderColor: 'var(--border-hover)',
          background: 'var(--bg-card)',
          color: 'var(--text)',
        }}
      />
    </div>
  )
}

interface EmptyStateProps {
  hasSearch: boolean
  t: TFunction<'cases'>
}

export function EmptyState({ hasSearch, t }: EmptyStateProps): ReactElement {
  return (
    <div className="py-12 text-center">
      <Briefcase className="mx-auto h-12 w-12" style={{ color: 'var(--text-muted)' }} />
      <h3 className="mt-4 text-lg font-medium" style={{ color: 'var(--text)' }}>
        {hasSearch ? t('orgCases.noMatchingCases') : t('orgCases.noCasesYet')}
      </h3>
      <p className="mt-2" style={{ color: 'var(--text-dim)' }}>
        {hasSearch ? t('orgCases.adjustSearch') : t('orgCases.createFirst')}
      </p>
    </div>
  )
}

interface OwnerSectionProps {
  owner: OrgCasesByOwner
  expanded: boolean
  onToggle: () => void
  onCaseClick: (caseId: string) => void
  theme: Theme
}

export function OwnerSection({
  owner,
  expanded,
  onToggle,
  onCaseClick,
  theme,
}: OwnerSectionProps): ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border)' }}>
      <OwnerHeader owner={owner} expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <div>
          {owner.cases.map((caseItem, idx) => (
            <div key={caseItem.id}>
              {idx > 0 && <div style={{ height: 1, background: 'var(--border)' }} />}
              <CaseRow
                caseItem={caseItem}
                onClick={() => {
                  onCaseClick(caseItem.id)
                }}
                theme={theme}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function OwnerHeader({
  owner,
  expanded,
  onToggle,
}: {
  owner: OrgCasesByOwner
  expanded: boolean
  onToggle: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between p-4 transition-colors"
      style={{ background: 'var(--table-header)' }}
      onMouseEnter={(event) => {
        event.currentTarget.style.background = 'var(--hover)'
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = 'var(--table-header)'
      }}
    >
      <div className="flex items-center gap-3">
        {expanded ? (
          <ChevronDown className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
        ) : (
          <ChevronRight className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
        )}
        <div className="flex items-center gap-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full"
            style={{ background: 'rgba(132, 204, 22, 0.15)' }}
          >
            <span className="text-sm font-medium" style={{ color: 'rgb(101, 163, 13)' }}>
              {owner.ownerName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="text-left">
            <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
              {t('orgCases.ownerCases', { name: owner.ownerName })}
            </h3>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {t('orgCases.caseCount', { count: owner.caseCount })} &middot;{' '}
              {formatBytes(owner.dataVolumeBytes)}
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="rounded-full px-2 py-1 text-xs font-medium"
          style={{ background: 'var(--highlight)', color: 'var(--text-dim)' }}
        >
          {owner.caseCount}
        </span>
      </div>
    </button>
  )
}

interface CaseRowProps {
  caseItem: OrgCaseEntry
  onClick: () => void
  theme: Theme
}

function CaseRow({ caseItem, onClick, theme }: CaseRowProps): ReactElement {
  const { t } = useTranslation('cases')
  const pColours = theme === 'dark' ? PRIORITY_COLOURS_DARK : PRIORITY_COLOURS_LIGHT
  const sColours = theme === 'dark' ? STATUS_COLOURS_DARK : STATUS_COLOURS_LIGHT
  const pStyle = pColours[caseItem.priority] ?? DEFAULT_BADGE
  const sStyle = sColours[caseItem.status] ?? DEFAULT_BADGE

  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between p-4 text-left transition-colors"
      style={{ background: 'var(--bg-card)' }}
      onMouseEnter={(event) => {
        event.currentTarget.style.background = 'var(--hover)'
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = 'var(--bg-card)'
      }}
    >
      <CaseRowMain caseItem={caseItem} t={t} />
      <div className="ml-4 flex items-center gap-2">
        <Badge bg={pStyle.bg} color={pStyle.text}>
          {caseItem.priority}
        </Badge>
        <Badge bg={sStyle.bg} color={sStyle.text}>
          {caseItem.status}
        </Badge>
      </div>
    </button>
  )
}

function CaseRowMain({
  caseItem,
  t,
}: {
  caseItem: OrgCaseEntry
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1 flex items-center gap-2">
        <h4 className="truncate font-medium" style={{ color: 'var(--text)' }}>
          {caseItem.name}
        </h4>
        {caseItem.orgCaseReference && (
          <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
            {caseItem.orgCaseReference}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        {caseItem.clientName && (
          <span className="flex items-center gap-1">
            <Briefcase className="h-3 w-3" />
            {caseItem.clientName}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Database className="h-3 w-3" />
          {t('card.artifacts', { count: caseItem.artifactCount })}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatRelativeTime(
            caseItem.lastActivityAt,
            t as (key: string, opts?: Record<string, unknown>) => string
          )}
        </span>
      </div>
    </div>
  )
}

function Badge({
  bg,
  color,
  children,
}: {
  bg: string
  color: string
  children: React.ReactNode
}): ReactElement {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-xs font-medium capitalize"
      style={{ background: bg, color }}
    >
      {children}
    </span>
  )
}
